// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file AuthContext.jsx
 * @description Context API para gestión de autenticación - Logout duro y definitivo
 * @version 4.0.0
 *
 * REGLAS:
 * - isLoggingOut bloquea cualquier restore/init para evitar rehidratación.
 * - Al logout: clearSession, API, clear caches, socket disconnect, redirect forzado.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { STORAGE_KEYS } from '../utils/constants';
import { apiService, clearApiCaches } from '../services/api';
import { logger } from '../utils/logger';
import { getAuthErrorKey } from '../utils/authErrors';
import { cleanUserForStorage } from '../utils/userNormalizer';
import { performLogout } from '../services/auth/LogoutService';
import socketService from '../services/socketService';
import { devLog } from '../utils/devConsoleLog';
import { pickCanonicalAvatarSourceUrl } from '../utils/avatarResolver';

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  console.warn('[RUNTIME FILE]', 'AuthContext.jsx v=' + import.meta.url);
}

/** Idempotencia initAuth ante StrictMode (doble mount) sin duplicar checkAuth/getProfile. */
let __authBootstrapKey = null;
let __authBootstrapPromise = null;
let __authBootstrapCompletedKey = null;

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  /** Error de acceso para mostrar en modal (ej. 403 cuenta no activada). */
  const [authError, setAuthError] = useState(null);
  /** Bloquea restore/init cuando es true; evita rehidratación tras logout. */
  const isLoggingOutRef = useRef(false);

  const mergeWithoutEmptyOverwrite = useCallback((base, incoming) => {
    const next = { ...(base || {}) };
    Object.keys(incoming || {}).forEach((key) => {
      const value = incoming[key];
      if (value === undefined || value === null) return;
      if (typeof value === 'string' && value.trim() === '') return;
      next[key] = value;
    });
    return next;
  }, []);

  const clearSession = useCallback(() => {
    devLog('[SET_USER_CALLED]', null);
    setUser(null);
    setIsAuthenticated(false);
    try {
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('archat_tenant_id');
      localStorage.removeItem('archat_csrf_token');
      // NO borrar archat_theme: el tema persiste tras logout
    } catch (e) {
      console.error('[AUTH] Error clearing storage in clearSession', e);
    }
    __authBootstrapKey = null;
    __authBootstrapPromise = null;
    __authBootstrapCompletedKey = null;
  }, []);

  /** Persistencia: SOLO el objeto que viene de /api/perfil.php (profile_avatar_full_url, avatar_version, etc.). Sin merge con login. */
  const persistSession = useCallback((profile, token) => {
    if (!profile) return;
    const userIdValue = (profile.id || profile.usuario_id)?.toString();
    if (!userIdValue) return;
    const toStore = { ...profile, usuario_id: profile.usuario_id ?? profile.id };
    setUser(toStore);
    setIsAuthenticated(true);
    const userId = toStore?.id ?? toStore?.usuario_id;
    if (typeof console !== 'undefined' && userId) {
      console.log('[AUTH_DEBUG_CURRENT_SESSION]', JSON.stringify({
        currentUserId: Number(userId),
        email: toStore?.email ?? toStore?.mail ?? null,
        nickname: toStore?.nick ?? toStore?.nickname ?? toStore?.name ?? null,
        idioma_preferido: toStore?.idioma_preferido ?? toStore?.language ?? null,
        currentLanguage: String(toStore?.idioma_preferido ?? toStore?.language ?? '').toLowerCase().trim().slice(0, 2) || null,
        tokenUserId: Number(userId),
        timestamp: new Date().toISOString(),
      }));
    }
    const cleaned = cleanUserForStorage(toStore);
    try {
      localStorage.setItem(STORAGE_KEYS.USER_ID, userIdValue);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(cleaned));
      if (token != null && token !== '') {
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      }
      const prefRaw = profile.idioma_preferido ?? profile.language ?? profile.lang ?? '';
      const pref = String(prefRaw).toLowerCase().trim().split(/[-_]/)[0] ?? '';
      if (pref.length >= 2) {
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, pref.slice(0, 2));
      }
    } catch (e) {
      logger.error('Error persistiendo sesión en localStorage', e);
    }
  }, []);

  const login = useCallback(async (username, password, email = null, captchaData = null) => {
    setAuthError(null);
    try {
      const response = await apiService.login(username, password, email, captchaData);

      const loginOk =
        response
        && response.success === true
        && !(typeof response.code === 'string' && (
          response.code === 'auth.invalid_credentials'
          || response.code === 'AUTH_INVALID_CREDENTIALS'
          || response.code === 'errors.auth_invalid_credentials'
        ));

      if (loginOk) {
        const userData = response.user || response.usuario;
        if (!userData) {
          return { success: false, code: 'auth.generic_error', status: 200, data: null, error: 'auth.generic_error', message: 'Error: No se recibieron datos del usuario' };
        }
        const needsTermsAcceptance =
          response.requires_terms_backend === true
          || (userData && Number(userData.terms_accepted) === 0);
        if (needsTermsAcceptance) {
          return {
            success: false,
            requiresTermsAcceptance: true,
            pendingGoogleData: { user: userData, token: response.token || null }
          };
        }

        const token = response.token ?? response.access_token ?? null;
        const userId = userData.id || userData.usuario_id;
        if (!token || !userId) {
          clearSession();
          return { success: false, code: 'auth.generic_error', status: 200, data: null, error: 'auth.generic_error', message: 'Token o user_id no recibidos' };
        }
        try {
          localStorage.removeItem('user');
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          localStorage.setItem(STORAGE_KEYS.TOKEN, token);
          localStorage.setItem(STORAGE_KEYS.USER_ID, String(userId));
        } catch (_) {}

        logger.info('[LOGIN_FLOW_FRONT] step=post_login_profile_request', {
          endpoint: '/api/perfil.php',
          userId,
          tokenPresent: !!token,
        });
        const profileResponse = await apiService.getProfile(userId);
        let profile;
        if (!profileResponse?.success) {
          logger.warning('[LOGIN_FLOW_FRONT] step=post_login_profile_error (fallback to login data)', {
            endpoint: '/api/perfil.php',
            userId,
            code: profileResponse?.code ?? profileResponse?.error ?? null,
            status: profileResponse?.status ?? null,
          });
          profile = userData;
        } else {
          profile = profileResponse.perfil || profileResponse.data || profileResponse.usuario || profileResponse;
        }
        if (!profile) {
          clearSession();
          return { success: false, code: 'auth.generic_error', status: 200, data: null, error: 'auth.generic_error', message: 'Failed to load profile after login' };
        }
        persistSession(profile, token);

        const invitationToken = localStorage.getItem('archat_invitation_token');
        if (invitationToken) {
          try {
            const acceptResponse = await apiService.acceptInvitation(invitationToken);
            if (acceptResponse.success) {
              localStorage.removeItem('archat_invitation_token');
            } else if (acceptResponse.code === 'CONTACT_LIMIT_REACHED' || acceptResponse.error?.includes('CONTACT_LIMIT_REACHED')) {
              localStorage.setItem('archat_contact_limit_error', 'true');
              localStorage.removeItem('archat_invitation_token');
            }
          } catch (invError) {
            if (invError.code === 'CONTACT_LIMIT_REACHED' || invError.message?.includes('CONTACT_LIMIT_REACHED')) {
              localStorage.setItem('archat_contact_limit_error', 'true');
            }
          }
        }

        try { window.dispatchEvent(new Event('archat:contact-updated')); } catch (_) {}
        return { success: true, code: 'auth.login_ok', status: 200, data: profile, user: profile, message: response.message || 'Login exitoso' };
      } else {
      // Interceptor ya mostró el modal global; no volver a setAuthError (evitar doble modal).
      const code = (typeof response.code === 'string' ? response.code : null) ?? getAuthErrorKey(response);
      const status = response.status ?? 0;
      const responseMessage = response.message ?? null;
      const noAltaCodes = ['TOKEN_INVALID', 'NO_SESSION', 'auth.invalid_credentials', 'AUTH_INVALID_CREDENTIALS', 'auth.generic_error'];
      const requiereRegistro = (response.requiere_registro === true || code === 'auth.require_email') && !noAltaCodes.includes(code);
      const result = {
        success: false,
        code: (typeof code === 'string' ? code : null) ?? 'auth.generic_error',
        status: status ?? 0,
        data: null,
        error: code ?? 'auth.generic_error',
        message: responseMessage,
        requiereRegistro,
        canRegister: response.can_register === true,
        userInactive: code === 'auth.user_inactive',
        tenantMismatch: code === 'auth.tenant_mismatch',
        cuentaInactiva: response.cuenta_inactiva ?? (code === 'auth.account_blocked' || code === 'auth.account_inactive_email_mismatch' || code === 'ACCOUNT_NOT_ACTIVATED'),
        activationToken: response.activation_token ?? null
      };
      console.error('[AUTH][CTX][LOGIN_FAIL]', result);
      return result;
      }
    } catch (err) {
      // Interceptor ya mostró el modal global; no setAuthError ni errorBus.emit (evitar doble modal).
      logger.error('Error en login (contraseña u OAuth)', err);
      const code = (typeof err?.code === 'string' ? err.code : null) ?? (typeof err?.data?.code === 'string' ? err.data.code : null) ?? (typeof err?.response?.data?.code === 'string' ? err.response?.data?.code : null) ?? getAuthErrorKey(err);
      const status = err?.status ?? err?.response?.status ?? 0;
      const responseMessage = err?.response?.data?.message ?? err?.data?.message;
      const noAltaCodes = ['TOKEN_INVALID', 'NO_SESSION', 'auth.invalid_credentials', 'AUTH_INVALID_CREDENTIALS', 'auth.generic_error'];
      const requiereRegistro = (err?.data?.requiere_registro === true || err?.response?.data?.requiere_registro === true || code === 'auth.require_email') && !noAltaCodes.includes(code);
      const result = {
        success: false,
        code: (typeof code === 'string' ? code : null) ?? 'auth.generic_error',
        status: status ?? 0,
        data: null,
        error: code ?? 'auth.generic_error',
        message: responseMessage ?? null,
        requiereRegistro,
        canRegister: err?.data?.can_register === true || err?.response?.data?.can_register === true,
        userInactive: code === 'auth.user_inactive',
        tenantMismatch: code === 'auth.tenant_mismatch',
        cuentaInactiva: err?.data?.cuenta_inactiva === true || err?.response?.data?.cuenta_inactiva === true || code === 'auth.account_blocked' || code === 'auth.account_inactive_email_mismatch' || code === 'ACCOUNT_NOT_ACTIVATED',
        activationToken: typeof err?.data?.activation_token === 'string' ? err.data.activation_token : (typeof err?.response?.data?.activation_token === 'string' ? err.response.data.activation_token : null)
      };
      console.error('[AUTH][CTX][LOGIN_FAIL]', result);
      return result;
    }
  }, [clearSession, persistSession]);

  /**
   * Login con Google (id_token). Llama a auth/google_login.php.
   * Si el backend indica usuario nuevo (usuario_nuevo/new_user/just_created), NO persiste y devuelve
   * requiresTermsAcceptance + pendingGoogleData para mostrar modal de términos en UI.
   * @param {string} idToken - id_token de Google Identity Services (credential.credential)
   * @returns {Promise<{ success: boolean, requiresTermsAcceptance?: boolean, pendingGoogleData?: { user, token } }>}
   */
  const loginWithGoogle = useCallback(async (idToken) => {
    try {
      const response = await apiService.googleLogin(idToken);
      if (response.success) {
        const userData = response.user || response.usuario;
        if (!userData) {
          return { success: false, code: 'auth.generic_error', status: 200, data: null, error: 'auth.generic_error' };
        }
        // Modal de términos: usuario nuevo (Google) o backend pide aceptación (requires_terms_backend)
        const isNewUser = response.usuario_nuevo === true || response.new_user === true || response.just_created === true;
        const requiresTermsBackend = response.requires_terms_backend === true;
        if (isNewUser || requiresTermsBackend) {
          return {
            success: false,
            requiresTermsAcceptance: true,
            pendingGoogleData: {
              user: userData,
              token: response.token || null
            }
          };
        }
        const token = response.token ?? response.access_token ?? null;
        const userId = userData.id || userData.usuario_id;
        if (!token || !userId) {
          return { success: false, code: 'auth.generic_error', status: 200, data: null, error: 'auth.generic_error' };
        }
        try {
          localStorage.removeItem('user');
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          localStorage.setItem(STORAGE_KEYS.TOKEN, token);
          localStorage.setItem(STORAGE_KEYS.USER_ID, String(userId));
        } catch (_) {}

        const profileResponse = await apiService.getProfile(userId);
        let profile;
        if (!profileResponse?.success) {
          profile = userData;
        } else {
          profile = profileResponse.perfil || profileResponse.data || profileResponse.usuario || profileResponse;
        }
        if (!profile) {
          return { success: false, code: 'auth.generic_error', status: 200, data: null, error: 'auth.generic_error', message: 'Failed to load profile after login' };
        }
        persistSession(profile, token);

        const invitationToken = localStorage.getItem('archat_invitation_token');
        if (invitationToken) {
          try {
            const acceptResponse = await apiService.acceptInvitation(invitationToken);
            if (acceptResponse.success) {
              localStorage.removeItem('archat_invitation_token');
            } else if (acceptResponse.code === 'CONTACT_LIMIT_REACHED' || acceptResponse.error?.includes('CONTACT_LIMIT_REACHED')) {
              localStorage.setItem('archat_contact_limit_error', 'true');
              localStorage.removeItem('archat_invitation_token');
            }
          } catch (invError) {
            if (invError.code === 'CONTACT_LIMIT_REACHED' || invError.message?.includes('CONTACT_LIMIT_REACHED')) {
              localStorage.setItem('archat_contact_limit_error', 'true');
            }
          }
        }
        try { window.dispatchEvent(new Event('archat:contact-updated')); } catch (_) {}
        return { success: true, code: 'auth.login_ok', status: 200, data: profile, user: profile };
      }
      const code = (typeof response.code === 'string' ? response.code : null) ?? getAuthErrorKey(response);
      return { success: false, code: code ?? 'auth.generic_error', status: response.status ?? 0, data: null, error: code ?? 'auth.generic_error' };
    } catch (err) {
      const code = (typeof err?.code === 'string' ? err.code : null) ?? (typeof err?.data?.code === 'string' ? err.data.code : null) ?? getAuthErrorKey(err);
      logger.error('Error en login con Google', err);
      return { success: false, code: code ?? 'auth.generic_error', status: err?.status ?? 0, data: null, error: code ?? 'auth.generic_error' };
    }
  }, [persistSession]);

  /**
   * Completa el alta de Google tras aceptación de términos: llama al backend accept_terms y persiste user/token.
   * @param {Object} pendingUser - user/usuario devuelto por googleLogin cuando requires_terms_backend
   * @param {string|null} pendingToken - token devuelto por googleLogin
   */
  const completeGoogleSignUpAfterTerms = useCallback(async (pendingUser, pendingToken) => {
    if (!pendingUser) return { success: false, error: 'pendingUser required' };
    const userIdValue = (pendingUser.id || pendingUser.usuario_id)?.toString();
    if (!userIdValue) return { success: false, error: 'user sin id' };
    if (pendingToken) {
      try {
        await apiService.acceptTerms(pendingToken);
      } catch (err) {
        logger.warning('accept_terms falló (se completa sesión igual)', err);
      }
    }
    try {
      localStorage.removeItem('user');
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    } catch (_) {}
    if (pendingToken) {
      try {
        localStorage.setItem(STORAGE_KEYS.TOKEN, pendingToken);
        localStorage.setItem(STORAGE_KEYS.USER_ID, String(pendingUser.id || pendingUser.usuario_id));
      } catch (_) {}
    }
    try {
      const profileResponse = await apiService.getProfile(pendingUser.id || pendingUser.usuario_id);
      if (profileResponse?.success) {
        const profile = profileResponse.perfil || profileResponse.data || profileResponse.usuario || profileResponse;
        if (profile) persistSession(profile, pendingToken ?? null);
        else persistSession(pendingUser, pendingToken ?? null);
      } else {
        persistSession(pendingUser, pendingToken ?? null);
      }
    } catch (_) {
      persistSession(pendingUser, pendingToken ?? null);
    }
    const invitationToken = localStorage.getItem('archat_invitation_token');
    if (invitationToken) {
      apiService.acceptInvitation(invitationToken).then((acceptResponse) => {
        if (acceptResponse?.success) localStorage.removeItem('archat_invitation_token');
        else if (acceptResponse?.code === 'CONTACT_LIMIT_REACHED' || acceptResponse?.error?.includes('CONTACT_LIMIT_REACHED')) {
          localStorage.setItem('archat_contact_limit_error', 'true');
          localStorage.removeItem('archat_invitation_token');
        }
      }).catch(() => {});
    }
    return { success: true, user: pendingUser };
  }, [persistSession]);

  /**
   * Hidrata sesión con token y user (p. ej. tras aceptar invitación cuando el backend devuelve token+user).
   * Si el backend devuelve user parcial, se hace merge con el user existente en localStorage para no perder avatar ni otros campos.
   */
  const loginWithToken = useCallback((token, userData) => {
    if (!token || !userData) {
      logger.warning('[AUTH] loginWithToken: token o userData faltante');
      return { success: false, error: 'token o user faltante' };
    }
    const userIdValue = (userData.id || userData.usuario_id)?.toString();
    if (!userIdValue) {
      logger.warning('[AUTH] loginWithToken: user sin id');
      return { success: false, error: 'user sin id' };
    }
    let toPersist = userData;
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (raw) {
        const existing = JSON.parse(raw);
        toPersist = { ...existing, ...userData };
      }
    } catch (_) {}
    persistSession(toPersist, token);
    if (typeof console !== 'undefined' && console.info) {
      console.info('[INVITE][FRONT] AUTO_LOGIN_OK user_id=' + userIdValue);
    }
    logger.info('[INVITE][FRONT] AUTO_LOGIN_OK', { user_id: userIdValue });
    return { success: true, user: toPersist };
  }, [persistSession]);

  const logout = useCallback(async () => {
    devLog('[ARCHAT][AUTH] logout requested');
    await performLogout();
  }, []);

  const updateUser = useCallback((userData) => {
    if (isLoggingOutRef.current) return;
    devLog('[SET_USER_CALLED]', userData);
    setUser(prev => {
      const merged = mergeWithoutEmptyOverwrite(prev, userData);
      merged.usuario_id = merged.usuario_id ?? merged.id;
      const canon = pickCanonicalAvatarSourceUrl(merged);
      if (canon && String(canon).trim() !== '') {
        const c = String(canon).trim();
        merged.profile_avatar_url = c;
        merged.profile_avatar_full_url = c;
        merged.avatar_url = c;
        merged.avatar = c;
        merged.foto_perfil = c;
      }
      const cleaned = cleanUserForStorage(merged);
      try {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(cleaned));
      } catch (e) {
        logger.warning('Error guardando user en updateUser', e);
      }
      return merged;
    });
  }, [mergeWithoutEmptyOverwrite]);

  /**
   * AVATAR CRITICAL PATH
   * Este método es la única fuente de verdad del avatar global tras subida exitosa (sesión + localStorage).
   * No actualizar estado si la URL no es string no vacía (evita regresiones y renders con src inválido).
   */
  const updateUserAvatar = useCallback((avatarUrl, avatarVersion) => {
    if (isLoggingOutRef.current) return;
    if (avatarUrl == null || avatarUrl === undefined) return;
    if (typeof avatarUrl !== 'string' || avatarUrl.trim() === '') return;
    setUser((prev) => {
      const next = {
        ...(prev || {}),
        profile_avatar_url: avatarUrl,
        avatar_url: avatarUrl,
        avatar: avatarUrl,
        foto_perfil: avatarUrl,
        profile_avatar_full_url: avatarUrl,
      };
      if (avatarVersion != null && avatarVersion !== '') {
        next.avatar_version = avatarVersion;
      }
      next.usuario_id = next.usuario_id ?? next.id;
      const cleaned = cleanUserForStorage(next);
      try {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(cleaned));
      } catch (e) {
        logger.warning('Error guardando user en updateUserAvatar', e);
      }
      if (typeof window !== 'undefined') {
        const targetUserId = Number(next.id ?? next.usuario_id ?? 0);
        if (Number.isFinite(targetUserId) && targetUserId > 0) {
          window.dispatchEvent(
            new CustomEvent('archat:avatar-updated', {
              detail: {
                userId: targetUserId,
                id: targetUserId,
                usuario_id: targetUserId,
                profile_avatar_url: avatarUrl,
                avatar_url: avatarUrl,
                avatar: avatarUrl,
                foto_perfil: avatarUrl,
                profile_avatar_full_url: avatarUrl,
                avatar_version: avatarVersion ?? next.avatar_version ?? null,
              },
            })
          );
        }
      }
      return next;
    });
  }, []);

  // Listener global: token inválido en cualquier request → logout duro (evitar sesión zombie)
  useEffect(() => {
    const onForceLogout = () => {
      if (isLoggingOutRef.current) return;
      console.warn('[AUTH][FORCE LOGOUT] Evento recibido; ejecutando logout');
      logout();
    };
    window.addEventListener('archat:force-logout', onForceLogout);
    return () => window.removeEventListener('archat:force-logout', onForceLogout);
  }, [logout]);

  useEffect(() => {
    const onAccountActivated = () => {
      if (isLoggingOutRef.current || !user) return;
      const userId = user.id || user.usuario_id;
      if (!userId) return;
      apiService.getProfile(userId).then((profileResponse) => {
        if (isLoggingOutRef.current) return;
        if (profileResponse && (profileResponse.success || profileResponse.data)) {
          const profileData = profileResponse.perfil || profileResponse.data || profileResponse.usuario || profileResponse;
          setUser(prev => {
            const merged = mergeWithoutEmptyOverwrite((prev || user), profileData);
            const canon = pickCanonicalAvatarSourceUrl(merged);
            if (canon && String(canon).trim() !== '') {
              const c = String(canon).trim();
              merged.profile_avatar_url = c;
              merged.profile_avatar_full_url = c;
              merged.avatar_url = c;
              merged.avatar = c;
              merged.foto_perfil = c;
            }
            devLog('[SET_USER_CALLED]', merged);
            const cleaned = cleanUserForStorage(merged);
            try {
              localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(cleaned));
            } catch (e) {
              logger.warning('Error saving user data after account-activated', e);
            }
            return merged;
          });
        }
      }).catch((err) => {
        logger.warning('Error refreshing profile on account-activated', err);
      });
    };
    window.addEventListener('archat:account-activated', onAccountActivated);
    return () => window.removeEventListener('archat:account-activated', onAccountActivated);
  }, [user, mergeWithoutEmptyOverwrite]);

  useEffect(() => {
    const initAuth = async () => {
      if (isLoggingOutRef.current) {
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      let userDataRaw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (!userDataRaw && typeof localStorage.getItem('user') === 'string') {
        userDataRaw = localStorage.getItem('user');
        try {
          localStorage.setItem(STORAGE_KEYS.USER_DATA, userDataRaw);
          localStorage.removeItem('user');
        } catch (_) {}
      }
      let userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
      if (!userId && userDataRaw) {
        try {
          const parsed = JSON.parse(userDataRaw);
          userId = (parsed.id || parsed.usuario_id)?.toString();
        } catch (_) {}
      }

      if (!token || !userId) {
        clearSession();
        setIsLoading(false);
        return;
      }

      if (isLoggingOutRef.current) {
        setIsLoading(false);
        return;
      }

      const bootstrapKey = `${userId}:${token}`;
      if (__authBootstrapCompletedKey === bootstrapKey) {
        console.log('[AUTH_FLOW][SKIP_DUPLICATE_PROFILE]');
        setIsLoading(false);
        return;
      }
      if (__authBootstrapPromise && __authBootstrapKey === bootstrapKey) {
        await __authBootstrapPromise;
        setIsLoading(false);
        return;
      }

      __authBootstrapKey = bootstrapKey;
      __authBootstrapPromise = (async () => {
        // Bootstrap: validar token con backend antes de restaurar sesión (evitar arranque con token muerto)
        let checkResult;
        try {
          checkResult = await apiService.checkAuth({ _skipForceLogout: true });
        } catch (checkErr) {
          if (checkErr?.code === 'TOKEN_INVALID' || checkErr?.status === 401) {
            clearSession();
            return;
          }
          // Error de red u otro: continuar con restauración; getProfile fallará luego si el token es inválido
        }

        // Si el backend devolvió success: false con code TOKEN_INVALID (sin lanzar), no restaurar
        if (checkResult && checkResult.success === false && (checkResult.code === 'TOKEN_INVALID' || checkResult.status === 401)) {
          clearSession();
          return;
        }

        if (isLoggingOutRef.current) {
          return;
        }

        try {
          const userIdNum = parseInt(userId, 10);
          if (!userIdNum) {
            return;
          }

          let profile = null;
          try {
            const profileResponse = await apiService.getProfile(userIdNum);
            if (isLoggingOutRef.current) return;
            if (profileResponse && (profileResponse.success || profileResponse.data)) {
              profile = profileResponse.perfil || profileResponse.data || profileResponse.usuario || profileResponse;
            }
          } catch (profileErr) {
            logger.warning('Error cargando perfil en initAuth, usando datos de localStorage', { message: profileErr?.message });
          }

          if (profile) {
            let mergedProfile = profile;
            try {
              const rawLs = localStorage.getItem(STORAGE_KEYS.USER_DATA);
              if (rawLs) {
                const ls = JSON.parse(rawLs);
                const lsVer = Number(ls.avatar_version) || 0;
                const pVer = Number(profile.avatar_version) || 0;
                const lsUrl = pickCanonicalAvatarSourceUrl(ls);
                if (lsVer > pVer && lsUrl) {
                  mergedProfile = {
                    ...profile,
                    profile_avatar_url: ls.profile_avatar_url || profile.profile_avatar_url,
                    avatar_url: ls.avatar_url || profile.avatar_url,
                    avatar: ls.avatar || profile.avatar,
                    foto_perfil: ls.foto_perfil || profile.foto_perfil,
                    profile_avatar_full_url:
                      ls.profile_avatar_full_url || ls.profile_avatar_url || profile.profile_avatar_full_url,
                    avatar_version: ls.avatar_version ?? lsVer,
                  };
                }
              }
            } catch (_) {
              /* ignore merge errors */
            }
            setUser(mergedProfile);
            setIsAuthenticated(true);
            const cleaned = cleanUserForStorage(mergedProfile);
            localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(cleaned));
          } else {
            const parsed = JSON.parse(userDataRaw);
            parsed.usuario_id = parsed.usuario_id ?? parsed.id;
            setUser(parsed);
            setIsAuthenticated(true);
          }
        } catch (error) {
          logger.error('Error parseando userData en initAuth', error);
          if (!isLoggingOutRef.current) {
            clearSession();
          }
        }
      })();

      try {
        await __authBootstrapPromise;
      } finally {
        __authBootstrapPromise = null;
      }

      try {
        if (
          localStorage.getItem(STORAGE_KEYS.TOKEN) &&
          localStorage.getItem(STORAGE_KEYS.USER_ID)
        ) {
          __authBootstrapCompletedKey = bootstrapKey;
        }
      } catch (_) {
        /* noop */
      }

      setIsLoading(false);
      try { window.dispatchEvent(new Event('archat:contact-updated')); } catch (_) {}
    };

    initAuth();
  }, [clearSession]);

  useEffect(() => {
    devLog('[AUTH USER]', user);
  }, [user]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    authError,
    setAuthError,
    login,
    loginWithGoogle,
    completeGoogleSignUpAfterTerms,
    loginWithToken,
    logout,
    updateUser,
    updateUserAvatar
  }), [user, isAuthenticated, isLoading, authError, login, loginWithGoogle, completeGoogleSignUpAfterTerms, loginWithToken, logout, updateUser, updateUserAvatar]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
